/**
 * Guide Book Sales — the box on the Retail season panel, and its PDF.
 *
 * The Guide Book is the Green Knight edition (PLU 11884 carries the book and
 * the price, 11885 the 10% contribution to the 75029 appeal). It is bought two
 * ways: in person at a shop, tea-room or booking-office till, or with an
 * online booking — in which case it is collected ("redeemed") when the booking
 * is redeemed at a booking office. The box shows the season total, the shown
 * period's total, and the online / in-person split; the PDF lists every outlet
 * with what it sold and how many online copies it handed over.
 */
import type { GbBuckets, GuideBooksResponse } from "./salesInsightsTypes";
import { pounds, prettyIso, savePdf, sectionTitle, startPdf, table } from "./salesPdf";

export interface GuideBookBoxProps {
  data: GuideBooksResponse | null;
  loading: boolean;
  error: string | null;
  rangeLabel: string;
  fmt: (n: number) => string;
}

function soldOf(b: GbBuckets): { qty: number; net: number } {
  return { qty: b.soldInPerson.qty + b.soldOnline.qty, net: b.soldInPerson.net + b.soldOnline.net };
}

export function GuideBookBox({ data, loading, error, rangeLabel, fmt }: GuideBookBoxProps) {
  const season = data ? soldOf(data.season) : null;
  const period = data ? soldOf(data.periodTotals) : null;
  const seasonIsPeriod = data ? data.period.from === `${data.fiscalYear - 1}-03-01` : false;
  return (
    <div className="sales-tile sales-tile--gc sales-tile--gb" title="Guide Book (Green Knight edition) sales — in person at the tills and with online bookings">
      <div className="sales-gc-head">
        Guide Book sales <span className="sales-gc-scope">season {data?.fiscalYearLabel ?? ""}</span>
      </div>
      {loading && !data ? (
        <div className="sales-gc-foot">Loading…</div>
      ) : error ? (
        <div className="sales-gc-foot sales-gc-foot--error">Couldn’t load Guide Book sales: {error}</div>
      ) : data && season && period ? (
        <>
          <div className="sales-gc-stats">
            <div className="sales-gc-stat sales-gc-stat--out">
              <span className="sales-gc-k">Season</span>
              <span className="sales-gc-v">{season.qty.toLocaleString()} <small>copies</small></span>
              <span className="sales-gc-k">{fmt(season.net)} net</span>
            </div>
            <div className="sales-gc-stat">
              <span className="sales-gc-k">{seasonIsPeriod ? "Shown period (whole season)" : rangeLabel}</span>
              <span className="sales-gc-v">{period.qty.toLocaleString()} <small>copies</small></span>
              <span className="sales-gc-k">{fmt(period.net)} net</span>
            </div>
          </div>
          <div className="sales-gc-foot">
            Season: online {data.season.soldOnline.qty.toLocaleString()} ({fmt(data.season.soldOnline.net)}) · in person {data.season.soldInPerson.qty.toLocaleString()} ({fmt(data.season.soldInPerson.net)}) · {data.season.redeemedOnline.qty.toLocaleString()} online copies collected at the booking offices
          </div>
        </>
      ) : (
        <div className="sales-gc-foot">No Guide Book sales recorded yet.</div>
      )}
      <div className="sales-gc-actions">
        <button type="button" className="sales-gc-btn" onClick={() => data && downloadGuideBookPdf(data, rangeLabel)} disabled={!data}
          title="Download a PDF: each outlet's Guide Book sales and collections, online versus in person, for the shown period and the season">
          PDF report
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The PDF — totals (online vs in person) then every outlet.
// ---------------------------------------------------------------------------
export function downloadGuideBookPdf(data: GuideBooksResponse, rangeLabel: string): void {
  const period = `${prettyIso(data.period.from)} to ${prettyIso(data.period.to)}`;
  const { doc, y: y0 } = startPdf(
    "Guide Book Sales",
    `Season ${data.fiscalYearLabel}  ·  shown period ${period} (${rangeLabel})  ·  Green Knight edition, sold in person at the tills or with an online booking and collected at a booking office`
  );
  const row = (label: string, b: GbBuckets) => {
    const sold = soldOf(b);
    return [label, b.soldInPerson.qty.toLocaleString(), pounds(b.soldInPerson.net), b.soldOnline.qty.toLocaleString(), pounds(b.soldOnline.net), sold.qty.toLocaleString(), pounds(sold.net), b.redeemedOnline.qty.toLocaleString()];
  };
  let y = sectionTitle(doc, "Totals", y0);
  y = table(doc, {
    startY: y,
    head: [["", "Sold in person", "£ net", "Sold online", "£ net", "Total sold", "£ net", "Online copies collected"]],
    body: [row(`Season ${data.fiscalYearLabel}`, data.season), row(`Shown period · ${period}`, data.periodTotals)],
    columnStyles: { 0: { halign: "left" } },
  });

  y = sectionTitle(doc, "By outlet", y);
  const tot = data.outlets.reduce(
    (a, o) => ({ ps: a.ps + o.period.soldQty, pn: a.pn + o.period.soldNet, pr: a.pr + o.period.redeemedQty, ss: a.ss + o.season.soldQty, sn: a.sn + o.season.soldNet, sr: a.sr + o.season.redeemedQty }),
    { ps: 0, pn: 0, pr: 0, ss: 0, sn: 0, sr: 0 }
  );
  table(doc, {
    startY: y,
    head: [[
      { content: "Outlet", rowSpan: 2, styles: { halign: "left" } },
      { content: `Shown period · ${period}`, colSpan: 3 },
      { content: `Season ${data.fiscalYearLabel}`, colSpan: 3 },
    ], ["Sold", "£ net", "Online copies collected", "Sold", "£ net", "Online copies collected"]],
    body: data.outlets.map((o) => [o.outlet, o.period.soldQty.toLocaleString(), pounds(o.period.soldNet), o.period.redeemedQty.toLocaleString(), o.season.soldQty.toLocaleString(), pounds(o.season.soldNet), o.season.redeemedQty.toLocaleString()]),
    foot: [["Total", tot.ps.toLocaleString(), pounds(tot.pn), tot.pr.toLocaleString(), tot.ss.toLocaleString(), pounds(tot.sn), tot.sr.toLocaleString()]],
    columnStyles: { 0: { halign: "left" } },
  });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.setTextColor(90, 90, 90);
  const finalY = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? y;
  doc.text(
    "“Sold” at an outlet = copies rung through that outlet’s till. “Online (internet sales)” = copies bought with an online booking. “Online copies collected” = online-bought copies handed over when the booking was redeemed at that till (no money — it was taken online).",
    10, Math.min(finalY + 6, doc.internal.pageSize.getHeight() - 10), { maxWidth: doc.internal.pageSize.getWidth() - 20 }
  );
  savePdf(doc, `guide-book-sales-${data.period.from}_to_${data.period.to}.pdf`);
}
